import React, { useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Map } from 'immutable'
import AmbassadorsYearDropdown from '../../AmbassadorsYearDropdown/index'
import Table from '../../../Table/index'
import { VALUED_CELL_MONEY_TYPE, ValuedCell } from '../../../Table/TableCell/index'
import { getAmbassadorPayouts } from '../../../../services/ambassadors/actions'
import {
  AMBASSADORS_PAGE_STATIC_KEY,
  PAYOUT_MIN,
  PAYOUTS_FIELD_ACTIVE,
  PAYOUTS_FIELD_END_DATE,
  PAYOUTS_FIELD_MONTH,
  PAYOUTS_FIELD_MONTHLY_EARNINGS,
  PAYOUTS_FIELD_TOTAL_EARNED,
  PAYOUTS_FIELD_TOTAL_UNPAID,
  PAYOUTS_FIELD_UNPAID_FROM_PREVIOUS,
  createCsvTitle,
  getCsvHeaders,
  getPayoutsCsvData,
  currentYear,
  formatAmount,
} from '../../../../services/ambassadors/index'
import {
  selectAmbassadorPayouts,
  selectAmbassadorPayoutsError,
  selectAmbassadorPayoutsProcessing,
  selectAmbassadorDetailsData,
} from '../../../../services/ambassadors/selectors'
import { selectStaticTextByKey } from '../../../../services/static-text/selectors'
import { selectLocale } from '../../../../services/page/index'
import { exportCSVFile } from '../../../../services/file/index'
import { format } from '../../../../services/date-time/index'

const NA = 'na'

const getTotalEarnedValue = (payout) => {
  if (Map.isMap(payout)) {
    return payout.get(PAYOUTS_FIELD_TOTAL_EARNED) + payout.get(PAYOUTS_FIELD_TOTAL_UNPAID)
  }
  return 0
}

/**
 * Render method for PAYOUTS_FIELD_TOTAL_EARNED column
 * @returns {JSX.Element}
 */
const renderTotalEarned = (value, item) => {
  if (value === NA) {
    return <span>&nbsp;</span>
  }
  const total = getTotalEarnedValue(item)
  const className = 'partner-payouts-tab__total-earned'
  const classNames = [className]
  if (total && total < PAYOUT_MIN) {
    classNames.push(`${className}--alert`)
  }
  return (
    <span className={classNames.join(' ')}>
      <ValuedCell value={total || value} type={VALUED_CELL_MONEY_TYPE} />
    </span>
  )
}

/**
 * Render method for PAYOUTS_FIELD_ACTIVE column
 * @returns {JSX.Element}
 */
const renderIsActive = (value, item) => {
  if (value === NA) {
    return <span>&nbsp;</span>
  }
  let statusText
  const staticText = useSelector(selectStaticTextByKey(AMBASSADORS_PAGE_STATIC_KEY))
  const status = item.get(PAYOUTS_FIELD_ACTIVE)
  const className = 'partner-payouts-tab__ambassador-status'
  const classNames = [className]
  if (status === true) {
    statusText = staticText.get('yes', '')
    classNames.push(`${className}--active`)
  } else if (status === false) {
    statusText = staticText.get('no', '')
    classNames.push(`${className}--inactive`)
  }
  return (
    <span className={classNames.join(' ')}>
      {statusText || <span>&mdash;</span>}
    </span>
  )
}

/**
 * Columns for the table
 * @returns {Array} array with the colums
 */
const getColumns = () => {
  const locale = useSelector(selectLocale)
  const staticText = useSelector(selectStaticTextByKey(AMBASSADORS_PAGE_STATIC_KEY))
  return [
    {
      key: PAYOUTS_FIELD_END_DATE,
      title: staticText.get('payoutsMonthColumn', ''),
      render: (value) => {
        return staticText.get(format(value, locale, 'MMMM')) || value
      },
    },
    {
      key: PAYOUTS_FIELD_UNPAID_FROM_PREVIOUS,
      title: staticText.get('payoutsUnpaidFromPreviousColumn', ''),
      render: (value) => {
        return value === NA
          ? <span>&nbsp;</span>
          : <ValuedCell value={value} type={VALUED_CELL_MONEY_TYPE} />
      },
    },
    {
      key: PAYOUTS_FIELD_MONTHLY_EARNINGS,
      title: staticText.get('payoutsMonthlyEarningsColumn', ''),
      render: (value) => {
        return value === NA
          ? <span>&nbsp;</span>
          : <ValuedCell value={value} type={VALUED_CELL_MONEY_TYPE} />
      },
    },
    {
      key: PAYOUTS_FIELD_TOTAL_EARNED,
      title: staticText.get('payoutsTotalEarnedColumn', ''),
      render: renderTotalEarned,
    },
    {
      key: PAYOUTS_FIELD_ACTIVE,
      title: staticText.get('payoutsActiveColumn', ''),
      render: renderIsActive,
    },
  ]
}

const PartnerPayoutsTab = ({ history }) => {
  const dispatch = useDispatch()

  const staticText = useSelector(selectStaticTextByKey(AMBASSADORS_PAGE_STATIC_KEY))
  const isPayoutsProcessing = useSelector(selectAmbassadorPayoutsProcessing)
  const payoutsError = useSelector(selectAmbassadorPayoutsError)
  const payoutsData = useSelector(selectAmbassadorPayouts)
  const ambassadorDetailsData = useSelector(selectAmbassadorDetailsData)
  const locale = useSelector(selectLocale)

  const [selectedYear, setSelectedYear] = useState(currentYear)

  const allPayoutsData = useMemo(() => {
    const includesCurrentMonth = !!(payoutsData.find((payout) => {
      return format(new Date(), locale, 'MMMM') === format(new Date(payout.get(PAYOUTS_FIELD_END_DATE)), locale, 'MMMM')
    }))
    return includesCurrentMonth ? payoutsData : payoutsData.unshift(Map({
      unpaidFromPreviousMonthUSD: null,
      currentMonthCommissionUSD: null,
      ambassadorIsActive: undefined,
      totalPayoutUSD: null,
      totalUnpaidUSD: null,
      endDate: new Date(),
      uuid: NA,
    }))
  }, [locale, payoutsData])

  const yearlyTotal = useMemo(() => payoutsData.reduce((acc, payout) => {
    const monthlyValue = payout.get(PAYOUTS_FIELD_MONTHLY_EARNINGS)
    return monthlyValue ? monthlyValue + acc : acc
  }, 0), [payoutsData])

  const summaryData = useMemo(() => Map({
    [PAYOUTS_FIELD_END_DATE]: `${selectedYear} ${staticText.get('total')}`,
    [PAYOUTS_FIELD_UNPAID_FROM_PREVIOUS]: NA,
    [PAYOUTS_FIELD_MONTHLY_EARNINGS]: yearlyTotal,
    [PAYOUTS_FIELD_TOTAL_EARNED]: NA,
    [PAYOUTS_FIELD_ACTIVE]: NA,
  }), [selectedYear, yearlyTotal])

  const formattedPayouts = useMemo(() => payoutsData.map((payout) => {
    const active = payout.get(PAYOUTS_FIELD_ACTIVE)
    return Map({
      [PAYOUTS_FIELD_MONTH]: staticText.get(format(payout.get(PAYOUTS_FIELD_END_DATE), locale, 'MMMM')),
      [PAYOUTS_FIELD_UNPAID_FROM_PREVIOUS]: formatAmount({
        amount: payout.get(PAYOUTS_FIELD_UNPAID_FROM_PREVIOUS),
        active,
        useSymbol: false,
      }),
      [PAYOUTS_FIELD_MONTHLY_EARNINGS]: formatAmount({
        amount: payout.get(PAYOUTS_FIELD_MONTHLY_EARNINGS),
        active,
        useSymbol: false,
      }),
      [PAYOUTS_FIELD_TOTAL_EARNED]: formatAmount({
        amount: getTotalEarnedValue(payout),
        active,
        useSymbol: false,
      }),
      [PAYOUTS_FIELD_ACTIVE]: active === true ? staticText.get('yes', '') : staticText.get('no', ''),
    })
  }), [payoutsData, staticText])

  const columns = getColumns(locale)

  const handleChangeYear = (selectedY) => {
    dispatch(getAmbassadorPayouts(selectedY))
    setSelectedYear(selectedY)
  }

  const handleExport = () => {
    const headers = getCsvHeaders(columns)
    const csvData = getPayoutsCsvData(formattedPayouts).toJS()

    const csvTitle = createCsvTitle({
      prefix: staticText.get('csvPartnerPrefix'),
      type: staticText.get('csvPayoutsTitle'),
      downloaded: staticText.get('Downloaded'),
      locale,
      name: ambassadorDetailsData.get('name', ''),
      year: selectedYear,
    })

    exportCSVFile(headers, csvData, csvTitle)
  }

  if (payoutsError) {
    return (
      <div>
        <div className="ambassadors-page__error">
          {staticText.get('somethingWentWrong')}
        </div>
      </div>
    )
  }

  return (
    <div className="partner-payouts-tab">
      <div className="ambassadors-page__selector-container">
        <AmbassadorsYearDropdown
          onSubmit={handleExport}
          onChange={handleChangeYear}
          buttonClass="form-button--secondary"
        />
      </div>
      <Table
        columns={columns}
        tableData={allPayoutsData}
        summaryRowData={summaryData}
        history={history}
        historyCallback={() => dispatch(getAmbassadorPayouts(selectedYear))}
        rowKey="uuid"
        loading={isPayoutsProcessing}
      />
      <div className="partner-payouts-tab__copy-container">
        <div className="partner-payouts-tab__copy--bullet">{staticText.get('payoutsCopy1')}</div>
        <div className="partner-payouts-tab__copy">{staticText.get('payoutsCopy2')}</div>
        <div className="partner-payouts-tab__copy">{staticText.get('payoutsCopy3')}</div>
      </div>
    </div>
  )
}

export default PartnerPayoutsTab
